import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  text: string;
  confirm: () => void | Promise<void>;
};

export const ConfirmationModal = ({ open, onClose, title, text, confirm }: Props) => {
  const { t } = useTranslation("common");
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (!open) setIsPending(false);
  }, [open]);

  const handleConfirm = async () => {
    const result = confirm();

    if (!(result instanceof Promise)) {
      onClose();
      return;
    }

    setIsPending(true);
    try {
      await result;
      onClose();
    } catch {
      // Keep the modal open so the user can retry or cancel.
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      dismissible={!isPending}
      title={title}
      description={text}
      footer={
        <div className="flex w-full flex-row justify-between gap-2">
          <Button variant="outline" onClick={onClose} disabled={isPending} className="flex-1">
            {t("actions.cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={isPending} className="flex-1">
            {isPending ? <Loader2 className="animate-spin" /> : t("actions.confirm")}
          </Button>
        </div>
      }
    />
  );
};
